import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  createTheme,
  ThemeProvider,
  getTheme,
  themes,
  densities,
} from '@weave-mui/styles';
import { CssBaseline } from '@weave-mui/material';

/**
 * WeaveMuiThemeProvider - A localized theme provider for weave-mui components
 *
 * This component provides weave-mui theming only to its children,
 * preventing global style conflicts with existing application styles.
 *
 * Usage:
 * <WeaveMuiThemeProvider>
 *   <ComponentUsingWeaveMui />
 * </WeaveMuiThemeProvider>
 */
const WeaveMuiThemeProvider = ({ children, includeCssBaseline = false }) => {
  const [theme, setTheme] = useState(() => createTheme({}));

  useEffect(() => {
    async function fetchTokens() {
      try {
        console.log('🎨 Loading Weave theme...');
        const weaveTheme = await getTheme(themes.LIGHT_GRAY, densities.MEDIUM);
        console.log('✅ Weave theme loaded:', weaveTheme);

        // Create a safe spacing function to prevent MUI errors with non-integer values
        // when weaveTheme's original spacing throws an error (due to using an array internally).
        const safeSpacing = (...args) => {
          return args
            .map((factor) => {
              if (typeof factor === 'string') return factor;

              // Try to use original spacing for integers
              if (Number.isInteger(factor)) {
                try {
                  return weaveTheme.spacing(factor);
                } catch (e) {
                  // Fallback to calculation
                }
              }

              // Calculate manually for non-integers or if original failed
              let baseStr = '8px';
              try {
                baseStr = weaveTheme.spacing(1) || '8px';
              } catch (e) {
                // Ignore error
              }

              const baseMatch = baseStr.toString().match(/([\d.]+)(.*)/);
              if (baseMatch) {
                const val = parseFloat(baseMatch[1]);
                const unit = baseMatch[2] || 'px';
                return `${val * factor}${unit}`;
              }

              return `${8 * factor}px`;
            })
            .join(' ');
        };

        // Start with Weave theme as base, then override with our custom primary color and safe spacing
        const combinedTheme = createTheme(weaveTheme, {
          spacing: safeSpacing,
          palette: {
            primary: {
              main: '#000000 !important',
              dark: '#333333 !important',
              light: '#666666 !important',
              contrastText: '#ffffff !important',
            },
          },
          components: {
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  borderColor: '#000000 !important',
                  '&:hover': {
                    borderColor: '#333333 !important',
                  },
                },
              },
            },
          },
        });

        console.log('🎯 Final combined theme:', combinedTheme);
        setTheme(combinedTheme);
      } catch (error) {
        console.error('❌ Failed to load Weave theme:', error);
        // Fallback to basic theme if Weave theme fails
        setTheme(createTheme({}));
      }
    }
    fetchTokens();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {includeCssBaseline && <CssBaseline />}
      {children}
    </ThemeProvider>
  );
};

WeaveMuiThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  includeCssBaseline: PropTypes.bool,
};

WeaveMuiThemeProvider.defaultProps = {
  includeCssBaseline: false,
};

export default WeaveMuiThemeProvider;
